import { api } from '@/utils/api';
import { Game } from '@/types/game';

/**
 * Service for handling game-related API calls
 */
export class GameService {
  /**
   * Get a game by its ID
   * @param gameId The ID of the game to retrieve
   * @returns Promise with the game data
   */
  static async getGameById(gameId: string): Promise<Game> {
    const response = await api.get(`/game/${gameId}`);
    return response.data;
  }

  /**
   * Get all pending games for the current user
   * @returns Promise with an array of pending games
   */
  static async getPendingGames(): Promise<Game[]> {
    const response = await api.get('/game/pending');
    return response.data;
  }

  /**
   * Create a new game
   * @param gameData The game data to create
   * @returns Promise with the created game data including gameId
   */
  static async createGame(gameData: Partial<Game>): Promise<Game> {
    const response = await api.post('/game', gameData);
    return response.data;
  }

  /**
   * Update an existing game
   * @param gameId The ID of the game to update
   * @param gameData The updated game data
   * @returns Promise with the updated game data
   */
  static async updateGame(gameId: string, gameData: Partial<Game>): Promise<Game> {
    const response = await api.put(`/game/${gameId}`, gameData);
    return response.data;
  }

  /**
   * Delete a game
   * @param gameId The ID of the game to delete
   * @returns Promise that resolves when the game is deleted
   */
  static async deleteGame(gameId: string): Promise<void> {
    await api.delete(`/game/${gameId}`);
  }

  /**
   * Update player status for a game invitation
   * @param gameId The ID of the game
   * @param status The new status ('accepted' or 'declined')
   * @returns Promise with the updated game data
   */
  static async updatePlayerStatus(gameId: string, status: 'accepted' | 'declined'): Promise<any> {
    const response = await api.put(`/game/${gameId}/player/status`, { status });
    return response.data;
  }

  /**
   * Update the score for a specific hole and player
   * @param gameId The ID of the game
   * @param holeNumber The hole number
   * @param playerId The player ID
   * @param scoreData The score data to update
   * @returns Promise with the updated game data
   */
  static async updateScore(
    gameId: string,
    holeNumber: number,
    playerId: string,
    scoreData: {
      strokes: number;
      putts?: number;
      penalties?: number;
      fairwayHit?: boolean;
      greenInRegulation?: boolean;
    }
  ): Promise<Game> {
    const response = await api.put(`/game/${gameId}/score`, {
      holeNumber,
      playerId,
      scoreData
    });
    return response.data;
  }
}
